import { Calendar, Clock, Tag, Repeat, MapPin, Globe, Users } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { formatPrice } from '@/shared/lib/format'
import type { Event } from '@/entities/event/model/types'
import { 
  getEventDate, 
  getEventTime, 
  getRecurrenceLabel,
  isRecurringEvent,
  hasEventDate,
  getSpotsLeft
} from '@/entities/event/lib/helpers'

export interface EventIdentityBlockProps {
  /** Событие */
  event: Event
}

/**
 * Виджет: Базовый информационный блок события
 * 
 * Отображает: название, категорию, дату (с учётом повторяемости), 
 * место, длительность, участники
 */
export function EventIdentityBlock({
  event,
}: EventIdentityBlockProps) {
  
  // Умное форматирование даты
  const eventDate = getEventDate(event)
  const eventTime = getEventTime(event)
  const recurrenceLabel = getRecurrenceLabel(event.recurrenceType)
  const isRecurring = isRecurringEvent(event)
  const hasDate = hasEventDate(event)
  const spotsLeft = getSpotsLeft(event)
  
  // Форматируем цену
  const priceText = event.isFree 
    ? 'Бесплатно' 
    : event.price 
      ? formatPrice(event.price) 
      : 'Уточняется'
  
  return (
    <div className="px-4 py-6 space-y-5">
      {/* Бейджи */}
      <div className="flex flex-wrap gap-2">
        {/* Повторяющееся событие */}
        {isRecurring && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium rounded-full">
            <Repeat className="w-4 h-4" />
            {recurrenceLabel}
          </span>
        )}
        
        {/* Онлайн */}
        {event.isOnline && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm font-medium rounded-full">
            <Globe className="w-4 h-4" />
            Онлайн
          </span>
        )}
        
        {/* Бесплатно */}
        {event.isFree && (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 text-sm font-medium rounded-full">
            Бесплатно
          </span>
        )}
      </div>
      
      {/* Название */}
      <h1 className="text-2xl font-semibold text-ink dark:text-white leading-tight">
        {event.title}
      </h1>
      
      {/* Мета-информация */}
      <div className="space-y-3">
        {/* Категория */}
        {event.category && (
          <div className="flex items-center gap-3 p-3 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Tag className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Категория</p>
              <p className="text-base font-medium text-ink dark:text-white">{event.category.name}</p>
            </div>
          </div>
        )}
        
        {/* Дата и время */}
        {hasDate && (
          <div className="flex items-center gap-3 p-3 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            {isRecurring ? (
              <Repeat className="h-5 w-5 text-purple-500 flex-shrink-0" />
            ) : (
              <Calendar className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
            )}
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">
                {isRecurring ? 'Расписание' : 'Дата и время'}
              </p>
              <p className="text-base font-medium text-ink dark:text-white">
                {eventDate}
                {eventTime && <span className="text-gray-500 dark:text-gray-400"> · {eventTime}</span>}
              </p>
            </div>
          </div>
        )}
        
        {/* Место */}
        {!event.isOnline && event.location && (
          <div className="flex items-center gap-3 p-3 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <MapPin className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Место</p>
              <p className="text-base font-medium text-ink dark:text-white">{event.location}</p>
            </div>
          </div>
        )}
        
        {/* Длительность */}
        {event.durationMinutes && (
          <div className="flex items-center gap-3 p-3 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Clock className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Длительность</p>
              <p className="text-base font-medium text-ink dark:text-white">{event.durationMinutes} минут</p>
            </div>
          </div>
        )}
        
        {/* Количество мест */}
        {event.maxParticipants && spotsLeft !== null && (
          <div className="flex items-center gap-3 p-3 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <Users className="h-5 w-5 text-primary dark:text-primary-light flex-shrink-0" />
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Свободных мест</p>
              <p className={cn(
                "text-base font-medium",
                spotsLeft === 0 
                  ? "text-red-600 dark:text-red-400" 
                  : spotsLeft <= 3 
                    ? "text-amber-600 dark:text-amber-400"
                    : "text-ink dark:text-white"
              )}>
                {spotsLeft === 0 ? 'Мест нет' : `${spotsLeft} из ${event.maxParticipants}`}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Цена (если не бесплатно) */}
      {!event.isFree && (
        <div className="flex items-baseline gap-2 pt-2">
          <span className="text-3xl font-bold text-primary dark:text-primary-light">
            {priceText}
          </span>
        </div>
      )}
    </div>
  )
}










